import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { pillars } from './content.js';
import type { Pillar } from './content.js';
import { PolicyDocument } from './LegalDocument.js';
import { BrandLockup, Signal } from './Signal.js';
import { SupportThread } from './SupportThread.js';
import { Partners } from './Partners.js';
import { Pricing } from './Pricing.js';
import privacyPolicy from '../../../docs/legal/PRIVACY-POLICY-publication-candidate.md';
import consumerHealthDataPolicy from '../../../docs/legal/CONSUMER-HEALTH-DATA-PRIVACY-POLICY-publication-candidate.md';
import accountDeletionPolicy from '../../../docs/legal/ACCOUNT-DELETION-publication-candidate.md';
import supportDocument from '../../../docs/legal/SUPPORT-publication-candidate.md';

/**
 * The current route.
 *
 * `initial` is what makes this renderable without a browser. The legal pages
 * are pre-rendered at build time so that a fetch which runs no JavaScript —
 * an app-store reviewer's crawler, most compliance scanners — receives the
 * policy text rather than an empty shell. Reading `window.location` during
 * render made that impossible: there is no window.
 *
 * The effect re-reads the real location on mount rather than trusting the
 * value it was given. A page pre-rendered at /privacy is served from
 * /privacy, so the two agree — but if they ever did not, the browser's own
 * address is the truth and the server's guess must not survive boot.
 */
function usePathname(initial?: string) {
  const [pathname, setPathname] = useState(
    initial ?? (typeof window === 'undefined' ? '/' : window.location.pathname),
  );
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', update);
    update();
    return () => window.removeEventListener('popstate', update);
  }, []);
  return pathname.replace(/\/+$/, '') || '/';
}

function useReveal() {
  useEffect(() => {
    const nodes = [...document.querySelectorAll<HTMLElement>('[data-reveal]')];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.dataset.revealed = 'true');
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        (entry.target as HTMLElement).dataset.revealed = 'true';
        observer.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  });
}

function navigate(event: React.MouseEvent<HTMLAnchorElement>) {
  const href = event.currentTarget.getAttribute('href');
  if (!href || !href.startsWith('/')) return;
  event.preventDefault();
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
  const hash = new URL(href, window.location.origin).hash.slice(1);
  window.requestAnimationFrame(() => {
    if (hash) document.getElementById(hash)?.scrollIntoView();
    else window.scrollTo({ top: 0, behavior: 'auto' });
  });
}

function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

function CinematicLoader({ onDone }: { onDone: () => void }) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setHidden(true);
      onDone();
      return;
    }
    const timer = window.setTimeout(() => {
      setHidden(true);
      onDone();
    }, 1900);
    return () => window.clearTimeout(timer);
  }, [onDone]);
  return (
    <div className={`cinematic-loader${hidden ? ' cinematic-loader--hidden' : ''}`} aria-hidden={hidden}>
      <div className="cinematic-loader__lockup" role="img" aria-label="Wellness Frontier">
        <Signal className="cinematic-loader__signal" />
        <div className="cinematic-loader__wordmark" aria-hidden="true">
          {[...'WELLNESS FRONTIER'].map((letter, index) => (
            <span className="cinematic-loader__letter" style={{ '--step': Math.abs(8 - index) } as React.CSSProperties} key={`${letter}-${index}`}>{letter === ' ' ? '\u00a0' : letter}</span>
          ))}
        </div>
      </div>
      <p>Built with frontier AI</p>
      <button type="button" onClick={() => { setHidden(true); onDone(); }}>Open homepage</button>
    </div>
  );
}

function SiteHeader({ floating = false }: { floating?: boolean }) {
  const [open, setOpen] = useState(false);
  const handleNavigate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    navigate(event);
  };
  return (
    <header className={`site-header${floating ? ' site-header--floating' : ''}`}>
      <a className="site-header__brand" href="/" onClick={handleNavigate}><BrandLockup /></a>
      <button className="site-header__menu" type="button" aria-expanded={open} aria-controls="site-navigation" onClick={() => setOpen((value) => !value)}>
        <span aria-hidden="true" /><span className="sr-only">Menu</span>
      </button>
      <nav className={open ? 'site-nav site-nav--open' : 'site-nav'} id="site-navigation" aria-label="Main navigation">
        <a href="/#experience" onClick={handleNavigate}>Experience</a>
        <a href="/#pillars" onClick={handleNavigate}>Seven pillars</a>
        <a href="/#employers" onClick={handleNavigate}>For employers</a>
        <a href="/#pricing" onClick={handleNavigate}>Pricing</a>
        <a href="/employee-login" onClick={handleNavigate}>Employee login</a>
        <a href="/employer-login" onClick={handleNavigate}>Employer login</a>
      </nav>
      <a className="button button--primary site-header__cta" href="/preview" onClick={handleNavigate}>Start a Risk-Free Preview</a>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__brand">
          <a href="/" onClick={navigate} aria-label="Wellness Frontier home"><BrandLockup /></a>
          <p>Personal wellness for employees. Clear program intelligence for employers.</p>
          <a className="site-footer__preview" href="/preview" onClick={navigate}>Start a Risk-Free Preview <Arrow /></a>
        </div>

        <nav className="site-footer__sitemap" aria-label="Footer sitemap">
          <section aria-labelledby="footer-explore">
            <h2 id="footer-explore">Explore</h2>
            <a href="/" onClick={navigate}>Home</a>
            <a href="/#experience" onClick={navigate}>Employee experience</a>
            <a href="/#pillars" onClick={navigate}>Wellness pillars</a>
            <a href="/#employers" onClick={navigate}>Employer analytics</a>
          </section>
          <section aria-labelledby="footer-access">
            <h2 id="footer-access">Access</h2>
            <a href="/preview" onClick={navigate}>Risk-Free Preview</a>
            <a href="/contact" onClick={navigate}>Request a Demo</a>
            <a href="/employee-login" onClick={navigate}>Employee login</a>
            <a href="/employer-login" onClick={navigate}>Employer login</a>
          </section>
          <section aria-labelledby="footer-company">
            <h2 id="footer-company">Company &amp; trust</h2>
            <a href="/contact" onClick={navigate}>Contact</a>
            {/*
              Contact is the demo request an employer fills in. Support is for
              the person using the app, and it is the Support URL the App Store
              is given, so it is on every route like the deletion link below.
            */}
            <a href="/support" onClick={navigate}>Support</a>
            <a href="/privacy" onClick={navigate}>Privacy Policy</a>
            <a href="/consumer-health-data-privacy" onClick={navigate}>Consumer Health Data Privacy</a>
            {/*
              Both app stores expect account deletion to be reachable from a
              public page without installing anything, so it is a footer link on
              every route rather than a paragraph inside the privacy policy.
            */}
            <a href="/delete-account" onClick={navigate}>Delete your account</a>
            <a href="/accessibility" onClick={navigate}>Accessibility</a>
            <a href="/#employers" onClick={navigate}>Employer visibility</a>
          </section>
        </nav>
      </div>

      <div className="site-footer__bottom">
        <p>© {new Date().getFullYear()} Wellness Frontier. All rights reserved.</p>
        <p>Built with frontier AI.</p>
        <p>Wellness guidance does not replace care from a doctor.</p>
      </div>
    </footer>
  );
}

function ProductPhone({ className = '' }: { className?: string }) {
  return (
    <figure className={`product-phone ${className}`}>
      <span className="product-phone__halo" aria-hidden="true" />
      <div className="product-phone__frame">
        {/*
          The wellness hub, not the home screen. This phone sits directly above
          the pillar rail, so showing the same seven areas the rail lists — in
          the app's own colours — makes the two read as one thing. The hero
          keeps the step-meter home screen; two identical screenshots of one
          screen said less than two screenshots of two.

          Captured from the real component by tools/capture-wellness.mjs, which
          is why the caption can say "actual".
        */}
        <img
          src="/public/media/employee-wellness-tiles-800w.webp"
          srcSet="/public/media/employee-wellness-tiles-400w.webp 400w, /public/media/employee-wellness-tiles-800w.webp 800w, /public/media/employee-wellness-tiles-1170w.webp 1170w"
          sizes="(max-width: 700px) 72vw, 400px"
          width="1170"
          height="2532"
          alt="Wellness Frontier employee app showing the seven wellness areas as tiles: physical, mental, spiritual, education, financial, occupational and environmental"
          loading="lazy"
          decoding="async"
        />
      </div>
      <figcaption>Actual employee experience</figcaption>
    </figure>
  );
}

function HeroProduct() {
  return (
    <figure className="hero-product" data-reveal>
      <div className="hero-product__field" aria-hidden="true">
        <span>Personal guidance</span>
        <span>Whole-person wellness</span>
      </div>
      <div className="hero-product__screen">
        <img
          src="/public/media/employee-home-metrics-800w.webp"
          srcSet="/public/media/employee-home-metrics-400w.webp 400w, /public/media/employee-home-metrics-800w.webp 800w, /public/media/employee-home-metrics-1170w.webp 1170w"
          sizes="(max-width: 700px) 62vw, 400px"
          width="1170"
          height="2532"
          alt="Wellness Frontier employee home screen: 10,395 steps against a 10,000 goal, with hydration and sleep beneath it"
        />
      </div>
      <figcaption><span>Actual product</span> Built around a person, not a points dashboard.</figcaption>
    </figure>
  );
}

function PillarDialog({ pillar, onClose }: { pillar: Pillar | null; onClose: () => void }) {
  const ref = useId();
  return pillar ? (
    <div className="pillar-sheet" role="dialog" aria-modal="true" aria-labelledby={ref} onClick={onClose}>
      <div className={`pillar-sheet__panel pillar-sheet__panel--${pillar.id}`} onClick={(event) => event.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="Close pillar details">×</button>
        <span className="pillar-sheet__symbol" aria-hidden="true">{pillar.symbol}</span>
        <p className="kicker">{pillar.eyebrow ?? 'A whole-person dimension'}</p>
        <h2 id={ref}>{pillar.dialogTitle ?? `${pillar.name} wellness`}</h2>
        <p>{pillar.detail}</p>
        <h3>Monthly experiences can include</h3>
        <ul>{pillar.examples.map((example) => <li key={example}>{example}</li>)}</ul>
        <small>Sample ideas. Employer reporting stays at the approved group level.</small>
      </div>
    </div>
  ) : null;
}

function PillarMosaic() {
  const [active, setActive] = useState<Pillar | null>(null);
  const rail = useRef<HTMLDivElement>(null);
  const move = (direction: -1 | 1) => {
    const width = rail.current?.clientWidth ?? 0;
    rail.current?.scrollBy({ left: direction * width * .78, behavior: 'smooth' });
  };
  return (
    <>
      <div className="pillar-rail-controls" aria-label="Browse wellness cards">
        <button type="button" onClick={() => move(-1)} aria-label="Previous wellness cards">←</button>
        <p>Swipe or use the arrows</p>
        <button type="button" onClick={() => move(1)} aria-label="Next wellness cards">→</button>
      </div>
      {/* data-reveal sits on the rail, not on each slide. Per-slide it lifted
          every tile 28px as it scrolled into view, so swiping sideways made
          the tiles bob upward one by one. */}
      <div className="pillar-rail" ref={rail} role="list" aria-label="Wellness dimensions and monthly challenges" data-reveal>
        {pillars.map((pillar, index) => (
          <div className="pillar-slide" role="listitem" key={pillar.name}>
            {/*
              NO aria-label. It replaced the accessible name with a
              description, so the name no longer contained the words on the
              card: the tile read "03 Spiritual Connect daily choices to
              meaning. Explore" while its name began "Spiritual. Spiritual
              wellness creates space for meaning...". A voice-control user
              saying "click Spiritual" activated nothing, which is WCAG 2.5.3
              Label in Name.

              The name now comes from the front face, so it IS the visible
              text and cannot drift from it. The longer description moves to
              aria-describedby, where a screen reader still reads it and it no
              longer competes with the label. The back face is already
              aria-hidden, so nothing is announced twice.
            */}
            <button
              className={`pillar-card pillar-card--${pillar.id}`}
              type="button"
              aria-describedby={`pillar-detail-${pillar.id}-${index}`}
              onClick={() => setActive(pillar)}
            >
              <span className="pillar-card__inner">
                <span className="pillar-card__face pillar-card__front">
                  <span className="pillar-card__number">{pillar.marker ?? String(index + 1).padStart(2, '0')}</span>
                  <span className="pillar-card__symbol" aria-hidden="true">{pillar.symbol}</span>
                  <strong>{pillar.name}</strong>
                  <span className="pillar-card__summary">{pillar.short}</span>
                  <small>Explore <Arrow /></small>
                </span>
                <span className="pillar-card__face pillar-card__back" aria-hidden="true">
                  <span className="pillar-card__back-label">Challenge examples</span>
                  <strong>{pillar.name}</strong>
                  <span>{pillar.detail}</span>
                  <span className="pillar-card__examples">{pillar.examples.map((example) => <span key={example}>{example}</span>)}</span>
                  <small>Open full details <Arrow /></small>
                </span>
              </span>
            </button>
            <span className="sr-only" id={`pillar-detail-${pillar.id}-${index}`}>
              {`${pillar.detail} Examples include ${pillar.examples.join(', ')}.`}
            </span>
          </div>
        ))}
      </div>
      <PillarDialog pillar={active} onClose={() => setActive(null)} />
    </>
  );
}

function HomePage() {
  const [loaderDone, setLoaderDone] = useState(false);
  useReveal();
  return (
    <>
      {!loaderDone ? <CinematicLoader onDone={() => setLoaderDone(true)} /> : null}
      <a className="skip-link" href="#main">Skip to main content</a>
      <div className="hero-wrap">
        <SiteHeader floating />
        <main id="main">
          <section className="hero" aria-labelledby="hero-title">
            <div className="hero__copy" data-reveal>
              <p className="kicker">Whole-person wellness, powered by frontier AI</p>
              <h1 id="hero-title">Wellness people use. Insight employers trust.</h1>
              <p className="hero__lede">Personal guidance for employees. Clear program intelligence for employers.</p>
              <div className="hero__actions">
                <a className="button button--primary" href="/preview" onClick={navigate}>Start a Risk-Free Preview</a>
                <a className="button button--secondary" href="/contact" onClick={navigate}>Request a Demo</a>
              </div>
            </div>
            <HeroProduct />
          </section>
        </main>
      </div>

      {/* Renders nothing until a customer has agreed to be named. See
          src/content.ts — the list is deliberately empty. */}
      <Partners />

      <section className="belief-strip" aria-label="A more complete wellness platform">
        <p>More than a step counter.</p><p>More personal than a portal.</p><p>More useful than another benefits PDF.</p>
      </section>

      <section className="experience" id="experience" aria-labelledby="experience-title">
        <div className="experience__copy" data-reveal>
          <p className="kicker">The employee experience</p>
          <h2 id="experience-title">A clearer next step, right when it matters.</h2>
          <p>Movement, hydration, rest, education, challenges, and support come together in one familiar place.</p>
          <div className="experience__moments">
            <article><span>01</span><h3>Choose where to begin</h3><p>Each person can start with what matters to them now.</p></article>
            <article><span>02</span><h3>Build a useful rhythm</h3><p>Small actions, practical education, and monthly challenges keep wellness approachable.</p></article>
            <article><span>03</span><h3>Keep the private parts private</h3><p>Personal questions and medical history are not employer reporting.</p></article>
          </div>
        </div>
        <ProductPhone className="experience__phone" />
      </section>

      <section className="pillars-section" id="pillars" aria-labelledby="pillars-title">
        <div className="section-heading" data-reveal>
          <p className="kicker">Whole-person wellness</p>
          <h2 id="pillars-title">Seven dimensions. Challenges that bring them to life.</h2>
          <p>Swipe through whole-person wellness and monthly action. Tap any card to explore.</p>
        </div>
        <PillarMosaic />

        {/*
          WHERE THE CHALLENGES ACTUALLY ARE.

          The rail claims monthly challenges and the screenshot in the section
          above shows seven areas and no sign of them, which was reported as a
          contradiction — fairly. Challenges are real but they live one tap in:
          the hub lists the areas, and opening one shows that month's actions
          with the buttons an employee presses. Claiming a feature next to a
          picture that omits it is weaker than showing it.

          Captured from the real screen by tools/capture-wellness.mjs with
          CAPTURE_VIEW=detail, using the app's own authored challenges.
        */}
        <figure className="pillars-proof" data-reveal>
          <div className="pillars-proof__frame">
            <img
              src="/public/media/employee-challenges-800w.webp"
              srcSet="/public/media/employee-challenges-400w.webp 400w, /public/media/employee-challenges-800w.webp 800w, /public/media/employee-challenges-1170w.webp 1170w"
              sizes="(max-width: 700px) 66vw, 320px"
              width="1170"
              height="2532"
              alt="An area opened in the employee app, showing this month's actions: walk ten minutes, read the sleep lesson, drink water on shift, stretch for five minutes, each with a button to mark it done"
              loading="lazy"
              decoding="async"
            />
          </div>
          <figcaption>
            <strong>Open any area to see the month.</strong> Small actions an employee
            marks done — the same seven areas, one tap in.
          </figcaption>
        </figure>
      </section>

      <section className="whats-new" id="whats-new" aria-labelledby="whats-new-title">
        <div className="section-heading">
          <p className="kicker">New in Wellness Frontier</p>
          <h2 id="whats-new-title">More reasons to open the app.</h2>
          <p>Every one keeps the same rule: what is personal stays with the employee.</p>
        </div>
        <div className="whats-new__grid">
          <article>
            <span className="whats-new__status whats-new__status--now">Available now</span>
            <h3>Shared recipes</h3>
            <p>Share a recipe and coworkers see it right away. The first one each week earns 100 points.</p>
          </article>
          <article>
            <span className="whats-new__status whats-new__status--now">For employers · available now</span>
            <h3>See what is working</h3>
            <p>How many people have joined and what gets used each month. Never one person's health details.</p>
          </article>
          <article>
            <span className="whats-new__status whats-new__status--now">Available now</span>
            <h3>Daily notes</h3>
            <p>A morning message and an evening note about your steps. Pick a journey, like the height of Everest, and watch your steps climb it.</p>
          </article>
          <article>
            <span className="whats-new__status whats-new__status--now">Available now · employer add-on</span>
            <h3>GLP-1 tracker</h3>
            <p>For people already taking a GLP-1 medicine. Log doses, symptoms, food and weight. It stays on your phone, and your employer never sees it or who uses it.</p>
          </article>
          <article>
            <span className="whats-new__status whats-new__status--now">Available now · employer add-on</span>
            <h3>AI personal trainers</h3>
            <p>Six AI trainers, each with its own style, to help plan your workouts. Always labelled as AI. Your employer never sees your chats.</p>
          </article>
        </div>
      </section>

      <section className="intelligence" aria-labelledby="intelligence-title">
        <div className="intelligence__copy" data-reveal>
          <p className="kicker">AI guidance with built-in safeguards</p>
          <h2 id="intelligence-title">Helpful by design. Limited on purpose.</h2>
          <p>Employees receive personalized wellness support. Employers receive approved aggregate insights. Individual health information stays private.</p>
          <div className="intelligence__principles" aria-label="AI guidance principles">
            <p><span>Guidance</span><strong>Plain-language education and practical next steps</strong></p>
            <p><span>Safety</span><strong>Does not diagnose or replace professional care</strong></p>
            <p><span>Privacy</span><strong>Individual health details stay out of employer reporting</strong></p>
          </div>
        </div>
        <div className="intelligence__map" data-reveal aria-label="How employee guidance stays separate from employer reporting">
          <div className="intelligence__lane intelligence__lane--employee">
            <span>Employee experience</span>
            <strong>Personalized wellness guidance</strong>
            <p>Education, challenges, and next steps shaped around the employee.</p>
          </div>
          <div className="intelligence__boundary">
            <span>Protected boundary</span>
            <strong>Personal questions and medical history are not employer reporting.</strong>
          </div>
          <div className="intelligence__lane intelligence__lane--employer">
            <span>Employer view</span>
            <strong>Approved aggregate program insights</strong>
            <p>Participation, adoption, and shared priorities across eligible groups.</p>
          </div>
        </div>
      </section>

      <section className="employer" id="employers" aria-labelledby="employer-title">
        {/*
          THE EMPLOYER CONSOLE, CAPTURED, NOT DESCRIBED. This section carried
          846 characters about analytics and no image at all — the half of the
          product the buyer personally uses was the half the site would not
          show, while the employee half had two real screenshots.

          The figures are invented and the caption says so out loud. Real
          tenant numbers are not ours to publish and the pilot organisation has
          not agreed to be named or quantified (operator, 2026-09-06). What is
          real is everything else: the components, the layout, the formatting
          rules, and the withheld row — a connected-app count below the group
          threshold renders as "Fewer than 11". That suppression is a thing
          being sold, so the screenshot shows it working rather than tidying it
          away. Captured by tools/capture-employer.mjs.
        */}
        <figure className="employer__shot" data-reveal>
          <img
            src="/public/media/employer-overview-1200w.webp"
            srcSet="/public/media/employer-overview-800w.webp 800w, /public/media/employer-overview-1200w.webp 1200w, /public/media/employer-overview-2368w.webp 2368w"
            sizes="(max-width: 980px) 92vw, 1100px"
            width="2368"
            height="2456"
            alt="The Wellness Frontier employer console: participation, total points, active people and connected-app adoption, with one app's count withheld as fewer than eleven"
            loading="lazy"
            decoding="async"
          />
          <figcaption>
            <strong>Sample data.</strong> The employer console as it ships. Figures are
            illustrative; one row is withheld because it falls below the group threshold.
          </figcaption>
        </figure>

        <div className="employer__copy" data-reveal>
          <p className="kicker">For employers</p>
          <h2 id="employer-title">Know what employees use. See what needs attention.</h2>
          <p>Track participation, program activity, adoption, and shared wellness priorities without exposing individual health information.</p>
          <details>
            <summary>What your employer can see <span aria-hidden="true">＋</span></summary>
            <div>
              <p><strong>Participation:</strong> active and eligible users, engagement rate, and approved organization or demographic group summaries.</p>
              <p><strong>Program activity:</strong> challenge engagement, special activity logs, points, and connected health app adoption.</p>
              <p><strong>Feature adoption:</strong> AI coach query counts and recipes shared.</p>
              <p><strong>Monthly direction:</strong> the wellness pillars employees say mattered most and group-level trends over time.</p>
              <p><strong>Never included:</strong> private questions, medical history, or personal health details.</p>
            </div>
          </details>
          <a className="text-link" href="/employer-login" onClick={navigate}>Open employer login <Arrow /></a>
        </div>
        <div className="employer__report" data-reveal aria-label="Employer analytics available in Wellness Frontier">
          <header>
            <span>Employer analytics</span>
            <strong>A clear view of program performance.</strong>
          </header>
          <div className="employer__report-grid">
            <article className="report-cell report-cell--participation">
              <span>Participation</span>
              <h3>Who is engaging</h3>
              <ul><li>Active vs eligible users</li><li>Engagement rate and trend</li><li>Approved organization, age, and sex groups</li></ul>
            </article>
            <article className="report-cell report-cell--activity">
              <span>Program activity</span>
              <h3>What gets used</h3>
              <p>Challenges, special activities, points, and connected health apps.</p>
            </article>
            <article className="report-cell report-cell--adoption">
              <span>Feature adoption</span>
              <h3>Where use is growing</h3>
              <p>AI coach query counts and recipes shared.</p>
            </article>
            <article className="report-cell report-cell--direction">
              <span>Monthly direction</span>
              <h3>What employees say mattered most</h3>
              <p>Aggregate wellness pillar priorities help shape the next month.</p>
            </article>
          </div>
          <p className="employer__privacy-note"><strong>Private by design:</strong> individual questions, medical history, and personal health details stay out of employer reporting.</p>
        </div>
      </section>

      <section className="support" aria-labelledby="support-title">
        <div className="support__intro" data-reveal>
          <h2 id="support-title">Wellness is the front door.</h2>
          <p>From there, employees can understand their benefits, organize private health information, and keep every next step in one trusted experience.</p>
        </div>
        <div className="support__story">
          <figure className="support__photo" data-reveal>
            <img src="/public/media/support-colleagues.webp" alt="Two colleagues reviewing useful information together during the workday." width="1600" height="763" loading="lazy" decoding="async" />
            <SupportThread />
          </figure>
          <div className="support__index" aria-label="Support available beyond the core wellness experience">
            <article data-reveal>
              <span>Benefits guidance</span>
              <h3>Benefits, made understandable.</h3>
              <p>Employees can ask about approved plan information in plain language, inside the same experience.</p>
            </article>
            <article data-reveal>
              <span>Private health organization</span>
              <h3>Health information stays employee-controlled.</h3>
              <p>Employees organize their own medical history. Employers cannot access it.</p>
            </article>
            <article data-reveal>
              <span>Access for every employer</span>
              <h3>A full program, sized for the business.</h3>
              <p>Small teams get the same thoughtful wellness experience as larger organizations.</p>
            </article>
          </div>
        </div>
      </section>

      <Pricing />

      <section className="final-cta" aria-labelledby="cta-title">
        <div><p className="kicker">Start with the experience</p><h2 id="cta-title">Give your people a wellness program worth opening.</h2></div>
        <div><p>Explore both sides with fictional data. No live employee information. No sales call required.</p><a className="button button--primary" href="/preview" onClick={navigate}>Start a Risk-Free Preview</a><a className="button button--secondary" href="/contact" onClick={navigate}>Request a Demo</a></div>
      </section>
      <SiteFooter />
    </>
  );
}

function InteriorShell({ eyebrow, title, copy, children }: { eyebrow: string; title: string; copy: string; children: ReactNode }) {
  return (
    <div className="interior-page">
      <a className="skip-link" href="#main">Skip to main content</a>
      <SiteHeader />
      <main id="main" className="interior-main">
        <header className="interior-hero"><div><p className="kicker">{eyebrow}</p><h1>{title}</h1><p>{copy}</p></div><Signal /></header>
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}

function LoginPage({ audience }: { audience: 'employee' | 'employer' }) {
  const [message, setMessage] = useState('');
  const isEmployee = audience === 'employee';
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('Secure sign-in is not connected in this marketing preview yet. Your information was not sent or stored.');
  };
  return (
    <InteriorShell eyebrow={`${isEmployee ? 'Employee' : 'Employer'} access`} title={`Welcome back to your ${isEmployee ? 'wellness experience' : 'program view'}.`} copy={isEmployee ? 'Your personal wellness information stays in the employee experience.' : 'Employer reporting is aggregate and follows role, tenant, authorization, and suppression rules.'}>
      <section className="form-card" aria-labelledby="sign-in-title">
        <div><p className="kicker">Secure access</p><h2 id="sign-in-title">{isEmployee ? 'Employee' : 'Employer'} sign in</h2><p>This page is ready for secure sign-in. This preview does not accept login details.</p></div>
        <form onSubmit={submit}><label>Email address<input type="email" name="email" autoComplete="username" required /></label><label>Password<input type="password" name="password" autoComplete="current-password" required /></label><button className="button button--primary" type="submit">Continue securely</button><p className="form-message" aria-live="polite">{message}</p></form>
      </section>
    </InteriorShell>
  );
}

function PreviewPage() {
  const { state, status, submit } = useContactForm('preview');
  return (
    <InteriorShell eyebrow="Risk-free preview" title="Explore the employee and employer experience." copy="The preview uses fictional data, no live integrations, and no real employee information.">
      <section className="preview-layout">
        <div className="preview-steps"><article><span>01</span><h2>Tell us who you are</h2><p>Full name, business name, and work email only.</p></article><article><span>02</span><h2>Verify your email</h2><p>A short-lived link confirms that the address belongs to you.</p></article><article><span>03</span><h2>See both sample views</h2><p>Explore the employee and employer experiences at your pace.</p></article></div>
        <form className="preview-form" onSubmit={submit}><p className="kicker">Start here</p><h2>Request preview access</h2><label>Full name<input type="text" name="name" autoComplete="name" required /></label><label>Business name<input type="text" name="business" autoComplete="organization" required /></label><label>Work email<input type="email" name="email" autoComplete="email" required /></label><Honeypot /><button className="button button--primary" type="submit" disabled={state === 'sending'}>{state === 'sending' ? 'Sending…' : 'Request preview access'}</button><p className="form-message" data-state={state} aria-live="polite">{status}</p><small className="form-note">We use this to reply to you. Nothing else. <a href="/privacy" onClick={navigate}>Privacy Policy</a></small></form>
      </section>
    </InteriorShell>
  );
}


/**
 * Submitting a form to /api/contact.
 *
 * THE STATE MACHINE IS THE POINT. A contact form's worst failure is not an
 * error — it is a cheerful "thanks, we'll be in touch" for a message that
 * reached nobody, because then the visitor stops trying and the business never
 * finds out. So success is only ever reported on a 2xx, and every other
 * outcome — no API key configured, provider down, function not deployed,
 * network gone — ends with the address to write to instead.
 */
type SendState = 'idle' | 'sending' | 'sent' | 'failed';

const CONTACT_ADDRESS = 'michael@wellnessfrontier.ai';

function useContactForm(source: string) {
  const [state, setState] = useState<SendState>('idle');
  const [detail, setDetail] = useState('');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (state === 'sending') return;
    const form = event.currentTarget;
    setState('sending');
    setDetail('');
    try {
      const body = new FormData(form);
      body.set('source', source);
      const response = await fetch('/api/contact', { method: 'POST', body });
      if (response.ok) {
        setState('sent');
        form.reset();
        return;
      }
      let reason = '';
      try {
        const payload = (await response.json()) as { error?: string };
        reason = payload?.error ?? '';
      } catch {
        /* a 404 from a missing function has no JSON body */
      }
      setState('failed');
      setDetail(reason);
    } catch {
      setState('failed');
      setDetail('');
    }
  };

  const status =
    state === 'sending'
      ? 'Sending…'
      : state === 'sent'
        ? 'Thank you — your message is on its way. We reply to every enquiry.'
        : state === 'failed'
          ? `${detail || 'That did not send.'} Please email ${CONTACT_ADDRESS} and we will pick it up there.`
          : '';

  return { state, status, submit };
}

/** The bait. Hidden from people and from assistive technology; bots fill it. */
function Honeypot() {
  return (
    <div aria-hidden="true" className="sr-only">
      <label>
        Company website
        <input type="text" name="company_website" tabIndex={-1} autoComplete="off" />
      </label>
    </div>
  );
}

function ContactPage() {
  const { state, status, submit } = useContactForm('contact');
  return (
    <InteriorShell eyebrow="Request a demo" title="Let’s talk about the people behind your business." copy="Tell us about your organization and what you want employees to experience.">
      <section className="form-card"><div><h2>A practical conversation.</h2><p>We will focus on your team, program goals, and the reporting boundary.</p></div><form onSubmit={submit}><label>Full name<input type="text" name="name" autoComplete="name" required /></label><label>Business name<input type="text" name="business" autoComplete="organization" required /></label><label>Work email<input type="email" name="email" autoComplete="email" required /></label><label>What would you like to explore?<textarea name="message" rows={4} /></label><Honeypot /><button className="button button--primary" type="submit" disabled={state === 'sending'}>{state === 'sending' ? 'Sending…' : 'Request a conversation'}</button><p className="form-message" data-state={state} aria-live="polite">{status}</p><small className="form-note">We use this to reply to you. Nothing else. <a href="/privacy" onClick={navigate}>Privacy Policy</a></small></form></section>
    </InteriorShell>
  );
}

function PrivacyPage() {
  return (
    <InteriorShell eyebrow="Privacy Policy" title="Your information. Clear boundaries." copy="This policy explains how Wellness Frontier collects, uses, protects, and shares information.">
      <PolicyDocument source={privacyPolicy} />
      <aside className="policy-related">
        <div><p className="kicker">Separate health-data notice</p><h2>Washington consumer health data</h2><p>Review the separate policy describing consumer health data under the Washington My Health My Data Act.</p></div>
        <a className="button button--primary" href="/consumer-health-data-privacy" onClick={navigate}>Read the health-data policy</a>
      </aside>
      <aside className="policy-related">
        <div><p className="kicker">Deletion</p><h2>Delete your account</h2><p>Section 8 describes the right; this page is how to use it, in the app or by email.</p></div>
        <a className="button button--primary" href="/delete-account" onClick={navigate}>How to delete your account</a>
      </aside>
    </InteriorShell>
  );
}

function ConsumerHealthDataPrivacyPage() {
  return (
    <InteriorShell eyebrow="Consumer Health Data Privacy" title="A separate notice for consumer health data." copy="This policy describes consumer health data collection, use, sharing, and Washington privacy rights.">
      <PolicyDocument source={consumerHealthDataPolicy} />
      <aside className="policy-related">
        <div><p className="kicker">Deletion</p><h2>Delete your account</h2><p>Ask us to delete your account and the information we hold about you, in the app or by email.</p></div>
        <a className="button button--primary" href="/delete-account" onClick={navigate}>How to delete your account</a>
      </aside>
    </InteriorShell>
  );
}

/*
 * Account deletion, on a public page of its own.
 *
 * Google Play requires a deletion route reachable from outside the app, and
 * Apple expects deletion to be findable rather than buried. The document is
 * markdown beside the other two policies, so all three are edited the same way
 * and rendered by the same component — a page hand-written in JSX here would
 * drift from the policies it has to agree with.
 */
function AccountDeletionPage() {
  return (
    <InteriorShell eyebrow="Account deletion" title="Delete your account." copy="How to ask us to delete your Wellness Frontier account, what is removed, and what is kept.">
      <PolicyDocument source={accountDeletionPolicy} />
      <aside className="policy-related">
        <div><p className="kicker">The full picture</p><h2>What we collect, and why</h2><p>The Privacy Policy describes everything we hold, what your employer can see, and the rights you have.</p></div>
        <a className="button button--primary" href="/privacy" onClick={navigate}>Read the Privacy Policy</a>
      </aside>
    </InteriorShell>
  );
}

/*
 * Support, on a public page of its own.
 *
 * Apple's App Store requires a Support URL that leads to real support
 * information, and /contact is a demo request for employers. The words are
 * markdown beside the policies for the reason the deletion page's are, and for
 * one more: the contact address stays in a document and out of this file.
 *
 * The renderer draws no links except that address, so the three pages the
 * document names are linked from the panels under it.
 */
function SupportPage() {
  return (
    <InteriorShell eyebrow="Help" title="Support" copy="Help with signing in, the health data on your phone, and your account.">
      <PolicyDocument source={supportDocument} />
      <aside className="policy-related">
        <div><p className="kicker">Deletion</p><h2>Delete your account</h2><p>Ask us to delete your account and the information we hold about you, in the app or by email.</p></div>
        <a className="button button--primary" href="/delete-account" onClick={navigate}>How to delete your account</a>
      </aside>
      <aside className="policy-related">
        <div><p className="kicker">The full picture</p><h2>What we collect, and why</h2><p>The Privacy Policy describes everything we hold, what your employer can see, and the rights you have.</p></div>
        <a className="button button--primary" href="/privacy" onClick={navigate}>Read the Privacy Policy</a>
      </aside>
      <aside className="policy-related">
        <div><p className="kicker">Separate health-data notice</p><h2>Consumer health data</h2><p>We have a separate policy for consumer health data. It covers the Washington My Health My Data Act.</p></div>
        <a className="button button--primary" href="/consumer-health-data-privacy" onClick={navigate}>Read the health-data policy</a>
      </aside>
    </InteriorShell>
  );
}

function AccessibilityPage() {
  return (
    <InteriorShell eyebrow="Accessibility" title="Wellness should work for more people." copy="We design the public experience and product interfaces to be understandable, keyboard-friendly, responsive, and respectful of reduced-motion preferences.">
      <section className="trust-page" aria-label="Accessibility commitment">
        <div className="trust-page__intro">
          <p className="kicker">Our commitment</p>
          <h2>Access is part of the experience.</h2>
          <p>We continue to test navigation, contrast, readable structure, touch targets, responsive layouts, and motion preferences as the platform grows.</p>
        </div>
        <div className="trust-page__grid">
          <article><span>Navigation</span><h3>Use the site your way.</h3><p>Core public journeys are designed for keyboard, touch, and modern assistive technology.</p></article>
          <article><span>Motion</span><h3>Your preference comes first.</h3><p>Reduced-motion settings remove nonessential transitions and cinematic movement.</p></article>
          <article><span>Feedback</span><h3>Tell us where access breaks down.</h3><p>If something is difficult to use, contact us with the page and device so the team can investigate.</p><a href="/contact" onClick={navigate}>Report an accessibility issue <Arrow /></a></article>
        </div>
      </section>
    </InteriorShell>
  );
}

function NotFoundPage() {
  return <InteriorShell eyebrow="Page not found" title="This path is beyond the map." copy="The public experience is still right here."><a className="button button--primary" href="/" onClick={navigate}>Return home</a></InteriorShell>;
}

export function App({ path: serverPath }: { path?: string }) {
  const path = usePathname(serverPath);
  if (path === '/') return <HomePage />;
  if (path === '/employee-login') return <LoginPage audience="employee" />;
  if (path === '/employer-login') return <LoginPage audience="employer" />;
  if (path === '/preview') return <PreviewPage />;
  if (path === '/contact') return <ContactPage />;
  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/consumer-health-data-privacy') return <ConsumerHealthDataPrivacyPage />;
  if (path === '/delete-account') return <AccountDeletionPage />;
  if (path === '/support') return <SupportPage />;
  if (path === '/accessibility') return <AccessibilityPage />;
  return <NotFoundPage />;
}
